import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useSearchParams } from 'react-router-dom';
import { buildPageSEO, tutorProfileSchema, breadcrumbSchema } from '@/utils/seo';
import {
  MapPin, Languages, Briefcase, Calendar, Star,
  User,
  Shield, AlertCircle, Heart, ChevronLeft, Monitor, Users
} from 'lucide-react';
import ReactCountryFlag from 'react-country-flag';
import verifiedIcon from '@/assets/verified.png';
import WeeklyTimeTable from '../components/common/WeeklyTimeTable';
import TutorDetailSkeleton from '../components/skeletons/TutorDetailSkeleton';
import WishlistCaseSelectionModal from '../components/Modal/WishlistCaseSelectionModal';
import { useTutorDetail } from '../hooks/api';
import {
  getOptimisticWishlistState,
  useTutorWishlistOverlayMap,
} from '@/hooks/api/useWishlistTutors';
import { useAuthContext, useModal } from '@/contexts';
import type { TutorData } from '../types/api';
import { groupSubjectsByActualRange } from '@/utils/subjectUtils';
import { getTuitionExperienceLabel, getTuitionExperienceShortLabel } from '@/utils/tuitionExperience';
import { useI18n } from '@/i18n';
import { localizeStringList, localizeText } from '@/i18n/options';
import { Link } from '@/i18n/navigation';
import { getWeeklyTimeTableI18nProps } from '@/utils/weeklyTimeTableI18n';

const NATIONALITY_MAP: Record<string, string> = {
  Malaysia: 'MY', Malaysian: 'MY',
  Singapore: 'SG', Singaporean: 'SG',
  'Hong Kong Chinese': 'HK', 'Hong Kong': 'HK',
  China: 'CN', Chinese: 'CN',
  Thailand: 'TH', Thai: 'TH',
  Vietnam: 'VN', Vietnamese: 'VN',
  India: 'IN', Indian: 'IN',
  Philippines: 'PH', Filipino: 'PH',
  Indonesia: 'ID', Indonesian: 'ID',
  'United Kingdom': 'GB', British: 'GB',
  'United States': 'US', American: 'US',
  Australia: 'AU', Australian: 'AU',
};

function getCountryCode(nationality: string): string | null {
  if (NATIONALITY_MAP[nationality]) return NATIONALITY_MAP[nationality];
  const lower = nationality.toLowerCase();
  for (const [key, code] of Object.entries(NATIONALITY_MAP)) {
    if (key.toLowerCase() === lower) return code;
  }
  if (nationality.length === 2) return nationality.toUpperCase();
  return null;
}

function mapTutorData(apiTutor: TutorData) {
  const location = apiTutor.preferredDistricts?.[0] ?? apiTutor.preferredState ?? 'N/A';
  const verified = !!apiTutor.isFullyVerified;
  const occupation = apiTutor.tutorExperienceTypes?.join(', ') || 'Tutor';
  const university = apiTutor.school || apiTutor.education || 'Not specified';
  const hourlyRate = typeof apiTutor.hourlyRate === 'number'
    ? apiTutor.hourlyRate.toString()
    : apiTutor.hourlyRate || '0';
  return { verified, location, occupation, university, hourlyRate };
}

function tuitionTypeLabel(type: string | undefined): string {
  if (type === 'both') return 'Physical & Online';
  if (type === 'physical') return 'Physical';
  if (type === 'online') return 'Online';
  return 'N/A';
}

const SectionCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6 ${className}`}>
    {children}
  </div>
);

const SectionHeading: React.FC<{ title: string }> = ({ title }) => (
  <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-4">{title}</h2>
);

const TutorProfilePage: React.FC = () => {
  const { locale } = useI18n();
  const weeklyTimeTableI18n = getWeeklyTimeTableI18nProps(locale);
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const source = searchParams.get('source') ?? undefined;
  const returnSearch = searchParams.get('returnSearch') ?? '';
  const returnTo = searchParams.get('returnTo') ?? '';
  const backToTutorsPath = returnSearch ? `/tutors?${returnSearch}` : '/tutors';
  const backLinkPath = returnTo || backToTutorsPath;
  const backLinkLabel =
    source === 'parent_my_cases_shortlist' && returnTo
      ? locale === 'zh-CN' ? '返回个案' : 'Back to Case'
      : locale === 'zh-CN' ? '返回导师列表' : 'Back to Tutors';
  const { data: tutor, isLoading, error } = useTutorDetail(id, source);
  const [imageLoadError, setImageLoadError] = useState(false);
  const { profile, firebaseUser } = useAuthContext();
  const { openLoginModal } = useModal();
  const wishlistOverlayMap = useTutorWishlistOverlayMap();
  const [isWishlistModalOpen, setIsWishlistModalOpen] = useState(false);
  const isZh = locale === 'zh-CN';

  const isParent = profile?.role === 'parent';
  const isTutor = profile?.role === 'tutor';
  const isWishlisted = tutor
    ? getOptimisticWishlistState(tutor.id, tutor.isWishlisted, wishlistOverlayMap)
    : false;
  const isMutating = tutor ? wishlistOverlayMap.has(tutor.id) : false;

  useEffect(() => {
    if (isTutor) return;
    document.body.classList.add('has-mobile-bottom-bar');
    return () => document.body.classList.remove('has-mobile-bottom-bar');
  }, [isTutor]);

  const handleInterestClick = () => {
    if (!firebaseUser) { openLoginModal(); return; }
    if (!isParent) return;
    if (!tutor || wishlistOverlayMap.has(tutor.id)) return;
    setIsWishlistModalOpen(true);
  };

  if (isLoading) return <TutorDetailSkeleton />;

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 max-w-md text-center">
        <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">{isZh ? '加载导师失败' : 'Failed to Load Tutor'}</h2>
        <p className="text-gray-500 text-sm mb-6">{error instanceof Error ? error.message : isZh ? '获取导师详情时发生错误' : 'An error occurred while fetching tutor details'}</p>
        <Link to={backLinkPath} className="inline-flex items-center gap-2 text-primary font-semibold hover:text-primary-600 transition-colors">
          <ChevronLeft className="w-4 h-4" /> {backLinkLabel}
        </Link>
      </div>
    </div>
  );

  if (!tutor) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 max-w-md text-center">
        <AlertCircle className="h-12 w-12 text-amber-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">{isZh ? '找不到导师' : 'Tutor Not Found'}</h2>
        <p className="text-gray-500 text-sm mb-6">{isZh ? '这位导师不存在，或已被移除。' : "This tutor doesn't exist or has been removed."}</p>
        <Link to={backLinkPath} className="inline-flex items-center gap-2 text-primary font-semibold hover:text-primary-600 transition-colors">
          <ChevronLeft className="w-4 h-4" /> {backLinkLabel}
        </Link>
      </div>
    </div>
  );

  const mapped = mapTutorData(tutor);
  const avatarUrl = tutor.profilePhotoUrl || tutor.profilePicture;
  const hasProfilePicture = Boolean(avatarUrl && !imageLoadError);
  const rating = tutor.rating ?? 0;
  const yearsOfExperience = tutor.yearsOfTuitionExperience ?? 0;
  const yearsOfExperienceLabel = getTuitionExperienceLabel(tutor.yearsOfTuitionExperience);
  const yearsOfExperienceShortLabel = getTuitionExperienceShortLabel(tutor.yearsOfTuitionExperience);
  const countryCode = tutor.nationality ? getCountryCode(tutor.nationality) : null;
  const hasTeachingStyle = tutor.teachingStyle && (
    (tutor.teachingStyle.iAm?.length ?? 0) > 0 ||
    (tutor.teachingStyle.iCan?.length ?? 0) > 0 ||
    (tutor.teachingStyle.iKnow?.length ?? 0) > 0
  );

  const actualRangeGroups = tutor.subjectCards ? groupSubjectsByActualRange(tutor.subjectCards) : [];
  const subjectList = actualRangeGroups.length > 0 ? actualRangeGroups.flatMap(g => g.subjects) : ['Tutoring'];
  const visibleFeatureTags = tutor.featureTags?.slice(0, 3) ?? [];
  const interestButtonClassName = `w-full py-3 flex items-center justify-center gap-2 rounded-xl text-sm font-semibold transition-all duration-200 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed ${
    isWishlisted
      ? 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100'
      : 'btn-primary'
  }`;
  const seo = buildPageSEO(
    `${tutor.fullName} – ${subjectList.slice(0, 3).join(', ')} Tutor in ${mapped.location}`,
    `${tutor.fullName} is a ${mapped.verified ? 'verified ' : ''}tutor offering ${subjectList.slice(0, 3).join(', ')} lessons in ${mapped.location}, Malaysia. ${yearsOfExperienceLabel ? `${yearsOfExperienceLabel} of experience.` : ''} From RM ${mapped.hourlyRate}/hr.`,
    `/tutor/${id}`,
    avatarUrl ?? undefined,
  );

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={seo.canonical} />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={seo.canonical} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        {seo.ogImage && <meta property="og:image" content={seo.ogImage} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        {seo.ogImage && <meta name="twitter:image" content={seo.ogImage} />}
        <script type="application/ld+json">
          {JSON.stringify(tutorProfileSchema({
            id: id!,
            fullName: tutor.fullName,
            subjects: subjectList,
            areas: tutor.preferredDistricts ?? [],
            hourlyRate: mapped.hourlyRate,
            verified: mapped.verified,
            rating: rating > 0 ? rating : undefined,
            yearsOfExperience,
            profilePicture: !imageLoadError ? avatarUrl : undefined,
          }))}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Tutors', path: '/tutors' },
            { name: tutor.fullName, path: `/tutor/${id}` },
          ]))}
        </script>
      </Helmet>
    <div className={`min-h-screen bg-gray-50 ${!isTutor ? 'pb-24 lg:pb-0' : ''}`}>

      {/* ── Hero Banner ── */}
      <div className="bg-gradient-to-br from-primary-50 via-hero-bg to-white border-b border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-10">

          {/* Breadcrumb */}
          <Link
            to={backLinkPath}
            className="inline-flex items-center gap-1.5 text-sm text-primary font-medium hover:text-primary-600 transition-colors mb-6 cursor-pointer"
          >
            <ChevronLeft className="w-4 h-4" />
            {backLinkLabel}
          </Link>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-8">

            {/* Avatar */}
            <div className="relative flex-shrink-0">
              {hasProfilePicture ? (
                <img
                  src={avatarUrl}
                  alt={tutor.fullName}
                  className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl object-cover border-4 border-white shadow-md"
                  onError={() => setImageLoadError(true)}
                />
              ) : (
                <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl border-4 border-white shadow-md bg-primary-100 flex items-center justify-center">
                  <User className="w-10 h-10 sm:w-12 sm:h-12 text-primary-400" />
                </div>
              )}
              {mapped.verified && (
                <img src={verifiedIcon} alt="Verified" className="absolute -bottom-2 -right-2 w-7 h-7" />
              )}
            </div>

            {/* Identity */}
            <div className="flex-grow min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">{tutor.fullName}</h1>
                {rating > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 rounded-full text-xs font-semibold text-amber-700">
                    <Star className="w-3 h-3 fill-current" />
                    {rating.toFixed(1)}
                  </span>
                )}
              </div>

              {/* Nationality */}
              {tutor.nationality && (
                <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-3">
                  {countryCode && (
                    <ReactCountryFlag
                      countryCode={countryCode}
                      svg
                      style={{ width: '1.1em', height: '1.1em' }}
                      title={tutor.nationality}
                    />
                  )}
                  <span>{tutor.nationality}</span>
                </div>
              )}

              {/* Occupation badge */}
	              <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary/10 text-primary rounded-full text-xs sm:text-sm font-medium mb-4">
	                <Briefcase className="w-3.5 h-3.5" />
	                {localizeText(locale, mapped.occupation)}
	              </div>

              {visibleFeatureTags.length > 0 && (
                <div className="mb-4 flex flex-wrap gap-2">
                  {visibleFeatureTags.map((tag) => (
                    <span
                      key={`${tag.code}-${tag.label}`}
                      className="inline-flex items-center rounded-lg border border-tutor-200 bg-tutor-50 px-2.5 py-1 text-[11px] sm:text-xs font-semibold text-tutor-800"
                    >
	                      {localizeText(locale, tag.label)}
                    </span>
                  ))}
                </div>
              )}

              {/* Quick stats row */}
              <div className="flex flex-wrap gap-3 sm:gap-4">
                <div className="flex items-center gap-1.5 text-primary">
                  <span className="font-bold text-xl sm:text-2xl">RM {mapped.hourlyRate}</span>
	                  <span className="text-xs sm:text-sm text-gray-500">{isZh ? '/小时' : '/hr'}</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <Calendar className="w-4 h-4 text-primary flex-shrink-0" />
	                  <span><span className="font-semibold text-gray-900">{yearsOfExperienceShortLabel ?? '—'}</span> {isZh ? '经验' : 'exp'}</span>
                </div>
                {(tutor.preferredState || (tutor.preferredDistricts && tutor.preferredDistricts.length > 0)) && (
                  <div className="flex items-center gap-1.5 text-sm text-gray-600">
                    <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>
                      {tutor.preferredDistricts && tutor.preferredDistricts.length > 0
                        ? `${tutor.preferredDistricts.slice(0, 2).join(', ')}${tutor.preferredDistricts.length > 2 ? ` +${tutor.preferredDistricts.length - 2}` : ''}`
	                        : tutor.preferredState ? localizeText(locale, tutor.preferredState) : tutor.preferredState}
                    </span>
                  </div>
                )}
                {tutor.tuitionType && (
                  <div className="flex items-center gap-1.5 text-sm text-gray-600">
                    <Monitor className="w-4 h-4 text-primary flex-shrink-0" />
	                    <span>{localizeText(locale, tuitionTypeLabel(tutor.tuitionType))}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Page Body ── */}
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">

          {/* ── Left Column ── */}
          <div className="lg:col-span-2 space-y-5">

            {/* Education & Qualifications */}
            <SectionCard>
	              <SectionHeading title={isZh ? '学历与资历' : 'Education & Qualifications'} />
              <div className="space-y-3">
                <div>
	                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">{isZh ? '大学 / 学校' : 'University / School'}</p>
                  <p className="text-sm sm:text-base text-gray-800 font-medium">{mapped.university}</p>
                </div>
                {tutor.major && (
                  <div>
	                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">{isZh ? '主修领域' : 'Field of Study'}</p>
                    <p className="text-sm sm:text-base text-gray-800 font-medium">{tutor.major}</p>
                  </div>
                )}
              </div>
            </SectionCard>

            {/* Subjects */}
            {actualRangeGroups.length > 0 && (
              <SectionCard>
	                <SectionHeading title={isZh ? '授课科目' : 'Subjects Taught'} />
                <div className="flex flex-col gap-4">
                  {actualRangeGroups.map(({ rangeLabel, subjects }) => (
                    <div key={`${rangeLabel}-${subjects.join('|')}`} className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3">
                      <span className="inline-flex w-fit items-center rounded-md border border-slate-200 bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 whitespace-nowrap flex-shrink-0">
	                        {localizeText(locale, rangeLabel)}
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {subjects.map((subject) => (
                          <span key={subject} className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700">
	                            {localizeText(locale, subject)}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            {/* Teaching Languages */}
            {tutor.teachingLanguages && tutor.teachingLanguages.length > 0 && (
              <SectionCard>
	                <SectionHeading title={isZh ? '授课语言' : 'Teaching Languages'} />
                <div className="flex flex-wrap gap-2">
	                  {localizeStringList(locale, tutor.teachingLanguages).map((lang, i) => (
                    <span key={i} className="px-3 py-1.5 bg-secondary/10 text-secondary border border-secondary/20 rounded-full text-xs sm:text-sm font-medium">
                      {lang}
                    </span>
                  ))}
                </div>
              </SectionCard>
            )}

            {/* Teaching Areas */}
            {(tutor.preferredState || (tutor.preferredDistricts && tutor.preferredDistricts.length > 0)) && (
              <SectionCard>
	                <SectionHeading title={isZh ? '授课地区' : 'Teaching Areas'} />
                <div className="flex flex-wrap gap-2 mb-4">
                  {tutor.preferredDistricts && tutor.preferredDistricts.length > 0
                    ? tutor.preferredDistricts.map((district, i) => (
                        <span key={i} className="px-3 py-1.5 bg-accent/10 text-accent-700 border border-accent/20 rounded-full text-xs sm:text-sm font-medium">
                          {district}
                        </span>
                      ))
                    : tutor.preferredState && (
                        <span className="px-3 py-1.5 bg-accent/10 text-accent-700 border border-accent/20 rounded-full text-xs sm:text-sm font-medium">
	                          {localizeText(locale, tutor.preferredState)}
                        </span>
                      )}
                </div>
                {tutor.tuitionType && (
                  <div className="px-4 py-2 bg-gray-50 rounded-xl border border-gray-200 text-sm text-gray-700">
	                    <span className="font-semibold text-gray-900">{isZh ? '授课方式：' : 'Tuition Type: '}</span>
	                    {localizeText(locale, tuitionTypeLabel(tutor.tuitionType))}
                  </div>
                )}
              </SectionCard>
            )}

            {/* Availability */}
            {tutor.availability && Object.keys(tutor.availability).length > 0 && (
              <SectionCard>
	                <SectionHeading title={isZh ? '偏好授课时间' : 'Preferred Teaching Time'} />
                <div className="overflow-x-auto">
                  <WeeklyTimeTable
                    selectedTimes={tutor.availability}
                    onChange={() => {}}
                    isEditable={false}
                    {...weeklyTimeTableI18n}
                  />
                </div>
              </SectionCard>
            )}

            {/* Exam Results */}
            {tutor.examResults && tutor.examResults.length > 0 && (
              <SectionCard>
	                <SectionHeading title={isZh ? '考试成绩' : 'Exam Results'} />
                <div className="space-y-4">
                  {tutor.examResults.map((result, i) => (
                    <div key={i} className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                      <p className="text-sm font-bold text-gray-800 mb-3">
	                        {localizeText(locale, result.examType)}
                        <span className="ml-2 text-xs font-medium text-gray-400">({result.year})</span>
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {result.results.map((r, j) => (
                          <div key={j} className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs shadow-sm">
	                            <span className="text-gray-600">{localizeText(locale, r.subject)}</span>
                            <span className="font-bold text-primary">{r.grade}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            {/* Teaching Style */}
            {hasTeachingStyle && (
              <SectionCard>
	                <SectionHeading title={isZh ? '教学风格' : 'Teaching Style'} />
                <div className="space-y-5">
                  {tutor.teachingStyle?.iAm && tutor.teachingStyle.iAm.length > 0 && (
                    <div>
	                      <p className="text-xs font-bold text-sky-600 uppercase tracking-widest mb-2.5">{isZh ? '我是' : 'I Am'}</p>
                      <div className="flex flex-wrap gap-2">
                        {tutor.teachingStyle.iAm.map((s, i) => (
	                          <span key={i} className="px-3 py-1.5 bg-sky-50 border border-sky-200 text-sky-800 rounded-full text-xs sm:text-sm font-medium">{localizeText(locale, s)}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {tutor.teachingStyle?.iCan && tutor.teachingStyle.iCan.length > 0 && (
                    <div>
	                      <p className="text-xs font-bold text-emerald-600 uppercase tracking-widest mb-2.5">{isZh ? '我可以' : 'I Can'}</p>
                      <div className="flex flex-wrap gap-2">
                        {tutor.teachingStyle.iCan.map((s, i) => (
	                          <span key={i} className="px-3 py-1.5 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-full text-xs sm:text-sm font-medium">{localizeText(locale, s)}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {tutor.teachingStyle?.iKnow && tutor.teachingStyle.iKnow.length > 0 && (
                    <div>
	                      <p className="text-xs font-bold text-violet-600 uppercase tracking-widest mb-2.5">{isZh ? '我了解' : 'I Know'}</p>
                      <div className="flex flex-wrap gap-2">
                        {tutor.teachingStyle.iKnow.map((s, i) => (
	                          <span key={i} className="px-3 py-1.5 bg-violet-50 border border-violet-200 text-violet-800 rounded-full text-xs sm:text-sm font-medium">{localizeText(locale, s)}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </SectionCard>
            )}

            {/* About Me */}
            <SectionCard>
	              <SectionHeading title={isZh ? '关于我' : 'About Me'} />
	              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
	                {tutor.aboutMe || (isZh ? '暂无相关介绍。' : 'No information provided.')}
	              </p>
            </SectionCard>
          </div>

          {/* ── Right Sidebar ── */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-20 z-sticky space-y-4">

              {/* Booking / Interest Card */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                {/* Card header gradient */}
                <div className="bg-gradient-to-r from-primary to-secondary px-5 py-4">
                  <div className="flex items-center gap-3">
                    {hasProfilePicture ? (
                      <img
                        src={avatarUrl}
                        alt={tutor.fullName}
                        className="w-10 h-10 rounded-xl object-cover border-2 border-white/30"
                        onError={() => setImageLoadError(true)}
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                        <User className="w-5 h-5 text-white" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-white font-semibold text-sm truncate">{tutor.fullName}</p>
                      <p className="text-white/60 text-xs truncate">#{tutor.id.slice(-6).toUpperCase()}</p>
                    </div>
                    {mapped.verified && (
                      <img src={verifiedIcon} alt="Verified" className="w-5 h-5 ml-auto flex-shrink-0" />
                    )}
                  </div>
                </div>

                {/* Stats grid */}
                <div className="grid grid-cols-2 gap-px bg-gray-100 border-b border-gray-100">
                  <div className="bg-white px-4 py-3 text-center">
                    <p className="text-lg sm:text-xl font-bold text-accent">RM {mapped.hourlyRate}</p>
	                    <p className="text-xs text-gray-400 mt-0.5">{isZh ? '每小时' : 'per hour'}</p>
                  </div>
                  <div className="bg-white px-4 py-3 text-center">
                    <p className="text-lg sm:text-xl font-bold text-gray-900">{yearsOfExperienceLabel ?? '—'}</p>
	                    <p className="text-xs text-gray-400 mt-0.5">{isZh ? '教学经验' : 'yrs experience'}</p>
                  </div>
                  <div className="bg-white px-4 py-3 text-center">
                    <p className="text-lg sm:text-xl font-bold text-gray-900">{rating > 0 ? rating.toFixed(1) : '—'}</p>
	                    <p className="text-xs text-gray-400 mt-0.5">{isZh ? '评分' : 'rating'}</p>
                  </div>
                  <div className="bg-white px-4 py-3 text-center">
                    <p className="text-lg sm:text-xl font-bold text-gray-900">{actualRangeGroups.length}</p>
	                    <p className="text-xs text-gray-400 mt-0.5">{isZh ? '科目分组' : 'subject groups'}</p>
                  </div>
                </div>

                {/* CTA */}
                {!isTutor && (
                  <div className="p-4">
                    <button
                      onClick={handleInterestClick}
                      disabled={isMutating}
                      className={interestButtonClassName}
                    >
                      <Heart className={`h-4 w-4 ${isWishlisted ? 'fill-current' : ''}`} />
	                      {isMutating ? (isZh ? '保存中…' : 'Saving…') : isWishlisted ? (isZh ? '已感兴趣' : 'Interested') : (isZh ? '我感兴趣' : "I'm Interested")}
                    </button>

                    <p className="text-center text-xs text-gray-400 mt-3 leading-relaxed">
	                      {isZh ? '我们的团队会跟进，协助你与这位导师对接。' : 'Our team will follow up to connect you with this tutor.'}
                    </p>
                  </div>
                )}
              </div>

              {/* Tutor fast-facts */}
              <div className="hidden lg:block bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-3">
	                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">{isZh ? '快速信息' : 'Quick Info'}</p>
                {(tutor.preferredState || (tutor.preferredDistricts && tutor.preferredDistricts.length > 0)) && (
                  <div className="flex items-start gap-2.5 text-sm">
                    <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">
                      {tutor.preferredDistricts && tutor.preferredDistricts.length > 0
                        ? tutor.preferredDistricts.slice(0, 3).join(', ')
	                        : tutor.preferredState ? localizeText(locale, tutor.preferredState) : tutor.preferredState}
                    </span>
                  </div>
                )}
                {tutor.teachingLanguages && tutor.teachingLanguages.length > 0 && (
                  <div className="flex items-start gap-2.5 text-sm">
                    <Languages className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
	                    <span className="text-gray-700">{localizeStringList(locale, tutor.teachingLanguages).join(', ')}</span>
                  </div>
                )}
                {tutor.tuitionType && (
                  <div className="flex items-start gap-2.5 text-sm">
                    <Monitor className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
	                    <span className="text-gray-700">{localizeText(locale, tuitionTypeLabel(tutor.tuitionType))}</span>
                  </div>
                )}
                <div className="flex items-start gap-2.5 text-sm">
                  <Users className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
	                  <span className="text-gray-700">{localizeText(locale, mapped.occupation)}</span>
                </div>
              </div>

              {/* Trust badge */}
              <div className="hidden lg:flex rounded-2xl border border-primary/20 bg-primary/5 p-4 flex-start gap-3">
                <Shield className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
	                  <p className="text-sm font-semibold text-primary mb-0.5">{isZh ? '已审核且安全' : 'Reviewed & Secure'}</p>
	                  <p className="text-xs text-gray-500 leading-relaxed">{isZh ? '所有导师都经过团队审核，你的资料也会受到保护。' : 'All tutors are reviewed by our team. Your information is protected.'}</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {!isTutor && (
        <div className="fixed inset-x-0 bottom-0 z-[1000] border-t border-gray-200 bg-white/95 px-4 py-3 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] backdrop-blur lg:hidden [padding-bottom:calc(0.75rem+env(safe-area-inset-bottom))]">
          <button
            onClick={handleInterestClick}
            disabled={isMutating}
            className={interestButtonClassName}
          >
            <Heart className={`h-4 w-4 ${isWishlisted ? 'fill-current' : ''}`} />
	            {isMutating ? (isZh ? '保存中…' : 'Saving…') : isWishlisted ? (isZh ? '已感兴趣' : 'Interested') : (isZh ? '我感兴趣' : "I'm Interested")}
          </button>
        </div>
      )}
      <WishlistCaseSelectionModal
        isOpen={isWishlistModalOpen}
        tutorId={tutor.id}
        tutorName={tutor.fullName}
        onClose={() => setIsWishlistModalOpen(false)}
      />
    </div>
    </>
  );
};

export default TutorProfilePage;
